import { PartBase } from "@/components/body/parts/part";
import Utils, { Colors } from "@/components/shared/util";
import { Patient } from "@/store/modules/patient/types";
import { TemperatureTypes } from "@/store/modules/user/types";
import { c2Requested } from "@/utilities/temperatureConversion";
import _ from "lodash";
import SVG from "svg.js";
import { Store } from "vuex";
import { BodyConfig } from ".";
import SvgBarGraph from "./svgBarGraph";

const TEMP_POS_X = 30;
const TEMP_POS_Y = 20;

export default class Temp extends PartBase {
  barGraph: SvgBarGraph;
  circle: SVG.Circle;
  riskPath: SVG.Path;
  unwatch: () => void;
  tempPreference: TemperatureTypes;

  constructor(config: BodyConfig, container: SVG.Container, store: Store<any>) {
    super(config, container, store);

    this.svgGroup = this.container.group();

    this.barGraph = new SvgBarGraph(
      this.container,
      { x: 5, y: 0, labelOffsetY: 20 },
      { title: "" },
      this.showGreenGrayOnly
    );
    const circlePositionX = 0;
    const circlePositionY = 38;
    const circleRadius = 20;

    this.circle = this.svgGroup
      .circle(circleRadius)
      .move(circlePositionX, circlePositionY);

    this.riskPath = this.svgGroup
      .path(
        "M0,38c-8.3,0-14.7-6.3-14.7-15.1c0-4.7,2.4-8.9,5.8-11.3V-29H9v40.6c3.4,2.3,5.7,7.3,5.7,11.3C14.7,31.3,8.2,38,0,38z"
      )
      .fill({ opacity: 0 })
      .move(-5, -4.5);

    this.svgGroup.add(this.barGraph.svgGroupRef());
    this.svgGroup.move(TEMP_POS_X, TEMP_POS_Y);

    this.tempPreference = this.store.getters["user/preferenceTemperature"];

    this.unwatch = this.store.watch(
      (_state, getters) => getters["user/preferenceTemperature"],
      newValue => this.updateTemp(newValue)
    );
  }

  updateTemp(newValue: TemperatureTypes) {
    this.tempPreference = newValue;
    this.barGraph.updateData(this.getTemp());
  }

  getTemp() {
    const updatedAtom = _.cloneDeep(this.atom);
    updatedAtom.value = c2Requested(updatedAtom.value, this.tempPreference);
    const displayValue = updatedAtom.value.toFixed(1);
    updatedAtom.legend = `${displayValue} °${this.tempPreference}`;
    return updatedAtom;
  }

  updateData(patient: Patient | null) {
    if (!patient) {
      this.barGraph.updateData();
      return;
    }

    this.atom = this.getAtom(patient);
    this.barGraph.updateData(this.getTemp());
    const level = (this.atom && this.atom.level) || 0;
    const color = Utils.getLevelColor(level, this.showGreenGrayOnly);

    this.circle.fill(color);

    this.risk = this.getRisk(patient);
    this.setRisk();

  }

  private setRisk() {
    const strokeSettings = this.risk
      ? {
          "stroke-width": "3.5px",
          stroke: "#ffa014"
        }
      : {
          "stroke-width": "1.5px",
          stroke: "#BEBEBE"
        };

    this.riskPath.attr(strokeSettings);
  }

  updateBrightnessMode() {
    super.updateBrightnessMode();
    const color = this.dark ? Colors.White : Colors.Black;
    this.barGraph.value1.attr({ fill: color });
    this.barGraph.value2.attr({ fill: color });
  }

  destroy() {
    this.unwatch();
    super.destroy();
  }
}
